import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PublicHeader, PublicFooter } from "@/components/layouts/PublicLayout";
import AuthenticatedPageLayout from "@/components/layouts/AuthenticatedPageLayout";
import { useAuth } from "@/context/AuthContext";
import { serviceList, SERVICE_DISCLAIMER } from "@/content/serviceDescriptions";
import { checkPricingEligibility } from "@/api/aimeansbusiness";
import useServiceModal from "@/hooks/useServiceModal";
import ServiceInfoModal from "@/components/modals/ServiceInfoModal";
import { ArrowRight, CheckCircle2, XCircle, AlertTriangle, Info, Lock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format price for display
const formatDynamicPrice = (price) => {
  if (!price) return "$--";
  return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

/**
 * PricingPage (Services Page) - Canonical v1.0
 * 
 * Displays the three stand-alone services with:
 * - GATED pricing (only visible post-evaluation + SER)
 * - What This Prevents (risk framing)
 * - What This Service Does NOT Include (scope boundaries)
 * - Decision Anchor (bottom of each card)
 * - Neutral CTAs only (pre-vet)
 * 
 * NO bundling, inheritance, sequencing, or implied upgrades.
 */
const PricingPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { activeServiceId, isOpen: isServiceModalOpen, openServiceModal, closeServiceModal } = useServiceModal();
  const [pricingVisible, setPricingVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dynamicPrices, setDynamicPrices] = useState({});
  const [pricesLoaded, setPricesLoaded] = useState(false);

  // Check pricing eligibility and fetch dynamic prices on mount
  useEffect(() => {
    const fetchData = async () => {
      // Fetch dynamic prices (public endpoint - always available)
      try {
        const pricesRes = await axios.get(`${API}/public/service-prices`);
        if (pricesRes.data) {
          setDynamicPrices(pricesRes.data);
          setPricesLoaded(true);
        }
      } catch (err) {
        console.error("Failed to fetch dynamic prices:", err);
        setPricesLoaded(true);  // Still mark as loaded to show fallback
      }

      // Check pricing visibility eligibility (requires auth)
      if (token) {
        try {
          const result = await checkPricingEligibility();
          setPricingVisible(result.pricing_eligible);
        } catch (err) {
          console.error("Failed to check pricing eligibility:", err);
          setPricingVisible(false);
        }
      } else {
        setPricingVisible(false);
      }
      setLoading(false);
    };
    fetchData();
  }, [token]);

  const handleGetStarted = () => {
    navigate('/get-started');
  };

  const content = (
    <>
      <section className="py-24 bg-gradient-to-b from-navy-950 to-navy-900">
        <div className="max-w-6xl mx-auto px-6">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <p className="text-primary font-medium mb-4 tracking-wide uppercase text-sm">
              Our Services
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight">
              Independent Decision-Support
              <span className="block text-primary">Not Consulting Ladders</span>
            </h1>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto">
              Each service is a complete, stand-alone engagement designed to reduce a specific class of business risk. 
              There is no bundling, no inheritance, and no forced sequencing. Choose what fits your situation.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {serviceList.map((service, index) => (
              <ServiceCard 
                key={service.id}
                service={service}
                isPopular={index === 1}
                pricingVisible={pricingVisible}
                onGetStarted={handleGetStarted}
                onLearnMore={() => openServiceModal(service.id)}
                dynamicPrices={dynamicPrices}
              />
            ))}
          </div>

          {/* Value Statement */}
          <div className="text-center mb-12">
            <blockquote className="text-xl md:text-2xl text-slate-300 italic leading-relaxed max-w-3xl mx-auto">
              &ldquo;The cost of good guidance is always less than the cost of a bad decision made without it.&rdquo;
            </blockquote>
          </div>

          {/* How It Works */}
          <div className="glass p-8 md:p-10 rounded-2xl mb-12" data-testid="pricing-process">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 text-center">
              How It Works
            </h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                {
                  step: "1",
                  title: "Free A.I. Business Evaluation",
                  description: "Complete a brief questionnaire about your business. We generate a personalized evaluation report at no cost."
                },
                {
                  step: "2",
                  title: "Review Your Report",
                  description: "Understand how A.I. affects your industry, your operations, and your competitive position."
                },
                {
                  step: "3",
                  title: "Discuss Your Options",
                  description: "If it makes sense to continue, we'll talk through which service fits your situation."
                },
                {
                  step: "4",
                  title: "Get Clarity",
                  description: "Work with us to cut through the noise and make confident decisions."
                }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary font-bold text-lg">{item.step}</span>
                  </div>
                  <h4 className="font-heading font-semibold text-white mb-2 text-sm">
                    {item.title}
                  </h4>
                  <p className="text-slate-400 text-xs leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="glass p-8 rounded-2xl border-primary/30 text-center" data-testid="pricing-cta">
            <h3 className="font-heading text-2xl font-bold text-white mb-4">
              Start With Clarity
            </h3>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto">
              Your free A.I. Business Evaluation costs nothing and commits you to nothing. 
              It&apos;s simply the first step toward understanding what A.I. means for your business.
            </p>
            <Link to="/get-started" data-testid="pricing-cta-btn">
              <Button className="bg-primary hover:bg-primary/90 text-white font-bold rounded-full px-10 py-6 text-lg shadow-[0_0_30px_rgba(217,119,6,0.4)] transition-all hover:scale-105">
                Get Your Free A.I. Business Evaluation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <p className="text-sm text-slate-500 mt-4">
              No credit card required. No sales pressure. Just insight.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="text-center mt-12">
            <p className="text-slate-500 text-xs">
              {SERVICE_DISCLAIMER}
            </p>
          </div>
        </div>
      </section>

      {/* Service Info Modal */}
      <ServiceInfoModal
        serviceId={activeServiceId}
        isOpen={isServiceModalOpen}
        onClose={closeServiceModal}
        pricingVisible={pricingVisible}
        dynamicPrices={dynamicPrices}
      />
    </>
  );

  // Render with authenticated layout if user is logged in
  if (user) {
    return <AuthenticatedPageLayout>{content}</AuthenticatedPageLayout>;
  }

  // Render with public layout for anonymous users
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      {content}
      <PublicFooter />
    </div>
  );
};

/**
 * ServiceCard - Canonical service card component with pricing gate
 * 
 * Required sections (in order):
 * 1. Headline + Short Description
 * 2. Price (GATED - shows placeholder when hidden)
 * 3. What You'll Receive (deliverables)
 * 4. What This Prevents (risk framing)
 * 5. What This Service Does NOT Include (mandatory)
 * 6. Important Clarification (B/C only)
 * 7. Decision Anchor (bottom)
 * 8. Neutral CTAs (no purchase CTA when price hidden)
 */
const ServiceCard = ({ service, isPopular, pricingVisible, onGetStarted, onLearnMore, dynamicPrices = {} }) => {
  // Get dynamic price for this service - ALWAYS use API price when available
  const dynamicPrice = dynamicPrices[service.id];
  const priceDisplay = dynamicPrice?.display || "Loading...";
  
  return (
    <div
      className={`glass rounded-2xl overflow-hidden flex flex-col ${
        isPopular ? "ring-2 ring-primary lg:scale-105" : ""
      }`}
      data-testid={`service-card-${service.id}`}
    >
      {/* Header: Headline + Description */}
      <div className="p-6 pb-4">
        {isPopular && (
          <span className="inline-block bg-primary text-navy-950 text-xs font-bold px-3 py-1 rounded-full mb-4">
            MOST REQUESTED
          </span>
        )}
        <h3 className="font-heading text-xl font-bold text-white mb-2">
          {service.title}
        </h3>
        <p className="text-primary font-semibold text-lg mb-2">
          {service.headline}
        </p>
        <p className="text-slate-400 text-sm leading-relaxed">
          {service.shortDescription}
        </p>
      </div>

      {/* Price Section - GATED */}
      <div className="px-6 py-4 bg-navy-950/50 border-y border-slate-700/50">
        {pricingVisible ? (
          <div className="flex items-baseline gap-2" data-testid={`price-visible-${service.id}`}>
            <span className="text-3xl font-bold text-white">
              {priceDisplay}
            </span>
            {dynamicPrice && <span className="text-slate-400 text-sm">fixed, prepaid</span>}
          </div>
        ) : (
          <div className="flex items-center gap-2" data-testid={`price-hidden-${service.id}`}>
            <Lock className="w-4 h-4 text-slate-500" />
            <span className="text-slate-400 text-sm">
              {service.pricing.displayLabelWhenHidden}
            </span>
          </div>
        )}
      </div>

      {/* What You'll Receive */}
      <div className="px-6 py-4">
        <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-400" />
          What You&apos;ll Receive
        </h4>
        <ul className="space-y-2">
          {service.deliverables.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-slate-400">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* What This Prevents - Risk Framing */}
      <div className="px-6 py-4 border-t border-slate-700/50 bg-slate-900/30">
        <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          What This Prevents
        </h4>
        <ul className="space-y-2">
          {service.whatThisPrevents.map((item, idx) => (
            <li key={idx} className="flex items-start gap-3 text-sm">
              <span className="text-slate-500 mt-0.5">•</span>
              <span className="text-slate-400">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* What This Service Does NOT Include - Mandatory */}
      <div className="px-6 py-4 border-t border-slate-700/50 bg-red-500/5">
        <h4 className="text-sm font-semibold text-red-400/80 mb-3 flex items-center gap-2">
          <XCircle className="w-4 h-4" />
          This Service Does NOT Include
        </h4>
        <ul className="space-y-2">
          {service.doesNotInclude.map((item, idx) => (
            <li key={idx} className="flex items-start gap-3 text-sm">
              <span className="text-red-400/50 mt-0.5">✕</span>
              <span className="text-slate-500">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Important Clarification (B/C only) */}
      {service.importantClarification && (
        <div className="px-6 py-4 border-t border-slate-700/50 bg-blue-500/5">
          <h4 className="text-sm font-semibold text-blue-400/80 mb-2 flex items-center gap-2">
            <Info className="w-4 h-4" />
            Important
          </h4>
          {service.importantClarification.map((line, idx) => (
            <p key={idx} className="text-slate-400 text-xs leading-relaxed">
              {line}
            </p>
          ))}
        </div>
      )}

      {/* Decision Anchor */}
      <div className="px-6 py-3 border-t border-slate-700/50 bg-navy-950/30">
        <p className="text-slate-500 text-xs italic text-center">
          {service.decisionAnchor}
        </p>
      </div>

      {/* CTAs - Neutral only when price hidden */}
      <div className="p-6 pt-4 border-t border-slate-700/50 mt-auto">
        <div className="flex flex-col gap-2">
          <Button
            onClick={onGetStarted}
            className={`w-full rounded-full py-5 font-semibold ${
              isPopular
                ? "bg-primary hover:bg-primary/90 text-white"
                : "bg-slate-700 hover:bg-slate-600 text-white"
            }`}
            data-testid={`service-cta-${service.id}`}
          >
            Start Free Evaluation
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            onClick={onLearnMore}
            className="w-full text-slate-400 hover:text-white text-sm"
            data-testid={`service-learn-more-${service.id}`}
          >
            Learn More
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
